import { Box, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

type ProductImagePlaceholderProps =
    | {
          variant?: "default";
          size?: number;
      }
    | {
          variant: "thumbnail";
          size?: never;
      };

/**
 * Square placeholder when no product photo is available (Figma `12819:67407` / `12819:62356`).
 */
export const ProductImagePlaceholder = (props: ProductImagePlaceholderProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isThumbnail = props.variant === "thumbnail";
    const thumbnail = theme.custom.mediaUpload.thumbnail;
    const defaultLayout = theme.custom.mediaUpload.default;
    const size = isThumbnail ? thumbnail.size : (props.size ?? defaultLayout.size);
    const stroke = isThumbnail ? thumbnail.stroke : defaultLayout.stroke;

    return (
        <Box
            role="img"
            aria-label={t("product.v2.illustration.placeholder", "Product illustration (upload coming soon)")}
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                position: "relative",
                border: isThumbnail ? "none" : `${stroke.width} ${stroke.style} ${stroke.color}`,
                borderRadius: theme.custom.mediaUpload.borderRadius,
                boxShadow: isThumbnail ? thumbnail.boxShadow : "none",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.custom.grey[2000],
                backgroundColor: "transparent",
            }}
        >
            <Typography
                variant={defaultLayout.emptyCopy.typographyVariant}
                sx={{
                    color: theme.custom.grey[2000],
                    textAlign: "center",
                    padding: theme.spacing(1),
                    lineHeight: defaultLayout.emptyCopy.lineHeight,
                }}
            >
                {t("product.v2.identity.add_picture", "Add a picture")}
            </Typography>
            {isThumbnail && (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: theme.spacing(0),
                        borderColor: stroke.color,
                        borderStyle: stroke.style,
                        borderWidth: stroke.width,
                        borderRadius: "inherit",
                        pointerEvents: "none",
                    }}
                />
            )}
        </Box>
    );
};
